import React, { useCallback, useEffect, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from "wavesurfer.js/plugins/regions";
import {ApplicationStore} from "../../../../store/ApplicationStore";


interface WaveformDisplayProps {
    clipId?: string;
    peaks: Float32Array | null;
    duration: number; // in seconds
    rangeEnabled: boolean;
    isAudioClip: boolean;
    selectedRange: { start: number; end: number } | null;
    waveformColor?: string; // Optional color for waveform (defaults to dark gray)
    gain?: number; // Optional gain for visual scaling (1.0 = normal, 2.0 = double height)
}

const getWaveformScaleY = (gain: number): number => {
    const g = Number.isFinite(gain) ? Math.max(0, gain) : 1;
    // Use sqrt so high gains are still noticeable without exploding visually.
    const scaled = Math.sqrt(Math.min(4, g));
    return Math.max(0.12, Math.min(2, scaled));
};

const WaveformDisplay: React.FC<WaveformDisplayProps> = ({ clipId, peaks, duration, rangeEnabled, selectedRange, waveformColor, gain = 1.0 }) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const wavesurferRef = useRef<WaveSurfer | null>(null);
	const regionsPluginRef = useRef<ReturnType<typeof RegionsPlugin.create> | null>(null);
	const activeRegionRef = useRef<any>(null);
	const dragSelectionUnsubRef = useRef<(() => void) | null>(null);
	const isReadyRef = useRef(false);
	const isInternalUpdateRef = useRef(false);
    const applyWaveformVerticalScaleRef = useRef<() => void>(() => {});
    
    // Store in refs to avoid stale closures
    const selectedRangeRef = useRef(selectedRange);
    selectedRangeRef.current = selectedRange;
	    const rangeEnabledRef = useRef(rangeEnabled);
	    rangeEnabledRef.current = rangeEnabled;

    const applyWaveformVerticalScale = useCallback(() => {
        const root = containerRef.current;
        if (!root) return;
        const scaleY = getWaveformScaleY(gain);
        const canvases = root.querySelectorAll('canvas');
        canvases.forEach((canvas) => {
            const el = canvas as HTMLCanvasElement;
            el.style.transformOrigin = '50% 50%';
            el.style.transform = `scaleY(${scaleY})`;
            el.style.willChange = 'transform';
        });
    }, [gain]);
    applyWaveformVerticalScaleRef.current = applyWaveformVerticalScale;

    // Sync selectedRange to visual region
    const syncRegionToSelection = () => {
        if (isInternalUpdateRef.current) return;
        if (!regionsPluginRef.current) return;

        const range = selectedRangeRef.current;
        const existing = activeRegionRef.current;

        // Clear selection → remove the existing region.
        if (!range) {
            try {
                existing?.remove?.();
            } catch {}
            activeRegionRef.current = null;
            return;
        }

        const { start, end } = range;
        const EPS = 1e-3;

        // If the existing region already matches the desired range, avoid re-creating it.
        // This prevents jitter during user drag updates when the store is updated continuously.
        if (
            existing &&
            typeof existing.start === 'number' &&
            typeof existing.end === 'number' &&
            Math.abs(existing.start - start) <= EPS &&
            Math.abs(existing.end - end) <= EPS
        ) {
            try {
                existing.drag = rangeEnabledRef.current;
                existing.resize = rangeEnabledRef.current;
            } catch {}
            return;
        }

        // Remove existing region before creating a new one.
        try {
            existing?.remove?.();
        } catch {}
        activeRegionRef.current = null;

        isInternalUpdateRef.current = true;
        try {
            activeRegionRef.current = regionsPluginRef.current.addRegion({
                id: 'external',
                start,
                end,
                drag: rangeEnabledRef.current,
                resize: rangeEnabledRef.current,
                color: 'rgba(144, 213, 255, 0.2)',
            });
        } catch (e) {
            console.log('[WaveformDisplay] Error adding region:', e);
        }
        setTimeout(() => { isInternalUpdateRef.current = false; }, 0);
    };

	useEffect(() => {
		if (!containerRef.current || !peaks) return;

		// Clean up any drag-selection handler from a previous instance.
		try {
			dragSelectionUnsubRef.current?.();
		} catch {}
		dragSelectionUnsubRef.current = null;

		// Clean up existing instance
		wavesurferRef.current?.destroy();

        // Use provided color or default to dark gray
        // waveformColor is expected to be a hex color (e.g., '#FF69B4')
        const defaultColor = 'rgba(0, 0, 26, 0.9)';
        let waveColor = defaultColor;
        let progressColor = defaultColor;
        
        if (waveformColor) {
            if (waveformColor.startsWith('#')) {
                // Hex color - convert to rgba with opacity
                const hex = waveformColor.slice(1);
                const r = parseInt(hex.slice(0, 2), 16);
                const g = parseInt(hex.slice(2, 4), 16);
                const b = parseInt(hex.slice(4, 6), 16);
                waveColor = `rgba(${r}, ${g}, ${b}, 0.9)`;
                progressColor = `rgba(${r}, ${g}, ${b}, 0.9)`;
            } else {
                // Assume it's already an rgba string
                waveColor = waveformColor;
                progressColor = waveformColor;
            }
        }

	        const baseBarHeight = 0.6;

	        // @ts-ignore
			const ws = WaveSurfer.create({
			container: containerRef.current,
			waveColor: waveColor,
			cursorWidth: 0,
			interact: false,
			height: "auto",
			normalize: false,
			barWidth: 0.0,
			barGap: 0.0,
	            barRadius: 0,
	            duration: duration,
	            peaks: [peaks],
	            barHeight: baseBarHeight,
	            mediaControls: false,
	        });

		ws.on('init', () => {
			wavesurferRef.current = ws;
			regionsPluginRef.current = ws.registerPlugin(RegionsPlugin.create());

			// Always attach region handlers so toggling the range tool doesn't require a full WaveSurfer re-init.
			regionsPluginRef.current.on('region-created', (region: any) => {
				// Clear any existing regions before creating a new one
				try {
					if (activeRegionRef.current && activeRegionRef.current !== region) {
						activeRegionRef.current.remove();
					}
				} catch (e) {
					console.log("Error calling remove on active region")
				}
				activeRegionRef.current = region;

				// Only update store if this was a USER-created region (drag selection)
				if (!isInternalUpdateRef.current) {
					isInternalUpdateRef.current = true;
					if (clipId && ApplicationStore.selectedClipId !== clipId) {
						ApplicationStore.setSelectedClipId(clipId);
					}
					ApplicationStore.setSelectedRange({ start: region.start, end: region.end });
					setTimeout(() => {
						isInternalUpdateRef.current = false;
					}, 0);
				}
			});

			regionsPluginRef.current.on('region-updated', (region: any) => {
				if (!isInternalUpdateRef.current) {
					isInternalUpdateRef.current = true;
					if (clipId && ApplicationStore.selectedClipId !== clipId) {
						ApplicationStore.setSelectedClipId(clipId);
					}
					ApplicationStore.setSelectedRange({ start: region.start, end: region.end });
					setTimeout(() => {
						isInternalUpdateRef.current = false;
					}, 0);
				}
			});
		});

	        ws.on('ready', () => {
	            isReadyRef.current = true;
	            // Sync any existing selection now that we're ready
	            syncRegionToSelection();
                applyWaveformVerticalScaleRef.current();
	        });

            ws.on('redraw', () => {
                applyWaveformVerticalScaleRef.current();
            });

	        ws.load('',
	            [peaks],   // ✅ passed to load()
	            duration)

        wavesurferRef.current = ws;

		return () => {
			ApplicationStore.setSelectedRange(null);
			try {
				dragSelectionUnsubRef.current?.();
			} catch {}
			dragSelectionUnsubRef.current = null;
			ws.destroy();
			isReadyRef.current = false;
		};
		}, [peaks, duration, waveformColor]); // NOTE: selectedRange/rangeEnabled are NOT dependencies

    // Gain visualization: cheap viewport-only scaling (avoid reloading peaks on gain changes).
    useEffect(() => {
        if (!wavesurferRef.current) return;
        applyWaveformVerticalScale();
        const id = window.requestAnimationFrame(() => applyWaveformVerticalScale());
        return () => window.cancelAnimationFrame(id);
    }, [gain, peaks, duration, applyWaveformVerticalScale]);

    // Separate effect: Sync external selectedRange changes to the visual region
    useEffect(() => {
        if (isReadyRef.current) {
            syncRegionToSelection();
        }
    }, [selectedRange, rangeEnabled]);

	useEffect(() => {
		const plugin = regionsPluginRef.current;
		const ws = wavesurferRef.current;
		if (!plugin || !ws) return;

		ws.setOptions({ interact: rangeEnabled });

		if (rangeEnabled) {
			if (!dragSelectionUnsubRef.current) {
				dragSelectionUnsubRef.current = plugin.enableDragSelection({
					color: 'rgba(0,0,0,0.3)',
					id: "draggable",
				});
			}
		} else {
			try {
				dragSelectionUnsubRef.current?.();
			} catch {}
			dragSelectionUnsubRef.current = null;

			try {
				if (activeRegionRef.current) {
					activeRegionRef.current.remove();
					activeRegionRef.current = null;
                }
            } catch (e) {
                console.log("Exception removing active region")
            }
            ApplicationStore.setSelectedRange(null);
        }
    }, [rangeEnabled]);

    // Click-away handler
    useEffect(() => {
        if (!rangeEnabled) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                // Check if the click is on the context menu
                const contextMenu = document.querySelector('.context-menu__content');
                if (contextMenu && contextMenu.contains(event.target as Node)) {
                    return; // Don't clear selection if clicking on context menu
                }
                
                // Clicked outside the waveform and not on context menu
                try {
                    if (activeRegionRef.current) {
                        activeRegionRef.current.remove();
                        activeRegionRef.current = null;
                    }
                } catch (e) {
                    console.log("Exception removing active region")
                }

                ApplicationStore.setSelectedRange(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [rangeEnabled]);

    return (
        <div
            ref={containerRef}
            style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, pointerEvents: rangeEnabled ? 'auto' : 'none' }}
        />
    );
};

export default WaveformDisplay;
